import { useState } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { api, formatDetail } from "../lib/api";
import useMeta from "../hooks/useMeta";

const REQUIRED_ACKS = [
  ["ack_liability_waiver", "I have read and agree to the Saviar Liability Waiver."],
  ["ack_assumption_of_risk", "I understand and accept the Assumption of Risk for participation."],
  ["ack_health_disclosure", "I have disclosed my health history, medications, and any conditions truthfully."],
  ["ack_participation_agreement", "I agree to the Saviar Participation Agreement and cohort community guidelines."],
  ["ack_privacy_policy", "I have read the Privacy Policy."],
  ["ack_terms", "My 25% deposit reserves my seat and room tier, with the balance due before the start date. Cancellation terms, including refunds, are set out in the Terms and Conditions."],
];

const ACK_LINKS = {
  ack_liability_waiver: ["Liability Waiver", "/liability-waiver"],
  ack_assumption_of_risk: ["Assumption of Risk", "/assumption-of-risk"],
  ack_participation_agreement: ["Participation Agreement", "/participation-agreement"],
  ack_privacy_policy: ["Privacy Policy", "/privacy-policy"],
  ack_terms: ["Terms and Conditions", "/terms-and-conditions"],
};

function AckLabel({ k, label }) {
  const link = ACK_LINKS[k];
  if (!link || !label.includes(link[0])) return label;
  const [before, after] = label.split(link[0]);
  return (
    <>
      {before}
      <a href={link[1]} target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold-light)", textDecoration: "underline" }} data-testid={`ack-link-${k}`}>{link[0]}</a>
      {after}
    </>
  );
}

export default function Apply() {
  useMeta("Apply | Saviar 21-Day Cohort",
    "Apply for the Saviar January 2027 founding cohort — six seats, Washington, DC area, January 6–29, 2027.");
  const [f, setF] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    if (!f.full_name || !f.email) { setError("Name and email are required."); setBusy(false); return; }
    for (const [k, label] of REQUIRED_ACKS) {
      if (!f[k]) { setError("Please confirm: " + label); setBusy(false); return; }
    }
    try {
      await api.post("/applications", { ...f, kind: "experience" });
      setSubmitted(true);
    } catch (err) { setError(formatDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const H = ({ n, t }) => (
    <div style={{ marginTop: 40, marginBottom: 20, paddingBottom: 12, borderBottom: "1px solid rgba(184,150,90,.25)" }}>
      <p className="eyebrow eyebrow-light">Section {n}</p>
      <h2 style={{ fontFamily: "var(--serif)", fontWeight: 300, fontSize: "1.5rem", color: "var(--ivory)", margin: "8px 0 0" }}>{t}</h2>
    </div>
  );

  return (
    <div data-testid="apply-page">
      <Nav />
      <section className="form-section">
        <div className="form-wrap">
          <p className="eyebrow eyebrow-light">21-Day Soft Life Reset — Application</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 300, color: "var(--ivory)", margin: "16px 0" }}>Apply for cohort admission.</h1>
          <p style={{ color: "rgba(250,246,238,.5)", marginBottom: 24, fontSize: ".92rem", lineHeight: 1.8 }}>
            Cohorts admit only 7 guests. Complete this intake so our team can determine safe fit. Documentation and any required screening typically take 2–3 weeks. All information is held in strict confidence.
          </p>

          {!submitted && (
            <div style={{ padding: 28, border: "1px solid rgba(184,150,90,.3)", background: "rgba(184,150,90,.06)", marginBottom: 40 }} data-testid="cohort-offer">
              <p className="eyebrow eyebrow-light" style={{ marginBottom: 10 }}>Founding Cohort</p>
              <h3 style={{ fontFamily: "var(--serif)", fontWeight: 300, color: "var(--ivory)", fontSize: "1.5rem", margin: "0 0 14px" }}>January 6 — 29, 2027 · Washington, DC area · 7 seats</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
                <div style={{ padding: 14, border: "1px solid rgba(184,150,90,.3)" }}>
                  <p style={{ fontSize: ".68rem", letterSpacing: ".2em", textTransform: "uppercase", color: "var(--gold-light)", margin: "0 0 6px" }}>Shared Room</p>
                  <p style={{ fontFamily: "var(--serif)", fontSize: "1.6rem", color: "var(--ivory)", margin: 0 }}>$6,500</p>
                  <p style={{ fontSize: ".78rem", color: "rgba(250,246,238,.55)", marginTop: 4 }}>Own bed · 25% deposit $1,625</p>
                </div>
                <div style={{ padding: 14, border: "1px solid var(--gold)" }}>
                  <p style={{ fontSize: ".68rem", letterSpacing: ".2em", textTransform: "uppercase", color: "var(--gold-light)", margin: "0 0 6px" }}>Private Room</p>
                  <p style={{ fontFamily: "var(--serif)", fontSize: "1.6rem", color: "var(--ivory)", margin: 0 }}>$9,500</p>
                  <p style={{ fontSize: ".78rem", color: "rgba(250,246,238,.55)", marginTop: 4 }}>Private room · 25% deposit $2,375</p>
                </div>
              </div>
              <p style={{ color: "rgba(250,246,238,.7)", fontSize: ".88rem", lineHeight: 1.75, margin: "0 0 10px" }}>
                <strong style={{ color: "var(--ivory)" }}>Included:</strong> your own bed, chef-prepared meals daily, the full cleanse and weight loss product protocol, and daily program sessions.
              </p>
              <p style={{ color: "rgba(250,246,238,.6)", fontSize: ".82rem", lineHeight: 1.75, margin: 0 }}>
                A 25% deposit reserves your seat and room tier, with the balance due before the start date. If you cancel 30 or more days before the start date, everything you paid is refunded in full. If you cancel within 30 days of the start date, anything paid beyond the deposit is refunded and the deposit is kept. If the cohort does not run as described, everything you paid, including the deposit, is refunded in full.
              </p>
            </div>
          )}

          {submitted ? (
            <div style={{ textAlign: "center", padding: "60px 0" }} data-testid="apply-success">
              <div style={{ width: 56, height: 56, border: "1px solid var(--gold)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", color: "var(--gold)" }}>✓</div>
              <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.8rem", fontWeight: 300, color: "var(--ivory)", marginBottom: 12 }}>Application received.</h3>
              <p style={{ color: "rgba(250,246,238,.5)", fontSize: ".92rem", maxWidth: 420, margin: "0 auto" }}>Our team reviews every intake personally. You'll hear back within 2–3 weeks with next steps or a scheduled screening call.</p>
              <Link to="/" className="btn btn-outline" style={{ marginTop: 32 }} data-testid="apply-return-home">Return Home</Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="apply-form">
              <H n="A" t="Contact" />
              <div className="form-group"><label>Full Name</label><input type="text" required onChange={set("full_name")} data-testid="input-name" /></div>
              <div className="form-group"><label>Email</label><input type="email" required onChange={set("email")} data-testid="input-email" /></div>
              <div className="form-group"><label>Phone</label><input type="tel" onChange={set("phone")} data-testid="input-phone" /></div>
              <div className="form-group"><label>City of Origin</label><input type="text" onChange={set("city_of_origin")} data-testid="input-city" /></div>
              <div className="form-group"><label>Date of Birth</label><input type="date" onChange={set("date_of_birth")} data-testid="input-dob" /></div>

              <H n="B" t="Goals & Program Alignment" />
              <div className="form-group"><label>What brought you to Saviar?</label><textarea onChange={set("what_brought_you")} data-testid="input-brought" /></div>
              <div className="form-group"><label>What does your soft life look like?</label><textarea onChange={set("soft_life_vision")} data-testid="input-vision" /></div>
              <div className="form-group">
                <label>Preferred start month</label>
                <select onChange={set("preferred_start_month")} data-testid="input-month">
                  <option value="">Select</option>
                  {["January","February","March","April","May","June","July","August","September","October","November","December"].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Prior program experience</label><textarea placeholder="Retreats, coaching, therapy, etc." onChange={set("prior_program_experience")} data-testid="input-prior" /></div>

              <H n="C" t="Health History & Medications" />
              <div className="form-group"><label>Current medical conditions</label><textarea onChange={set("health_history")} data-testid="input-health" /></div>
              <div className="form-group"><label>Current medications & supplements</label><textarea onChange={set("medications")} data-testid="input-meds" /></div>
              <div className="form-group"><label>Dietary restrictions & allergies</label><textarea onChange={set("dietary_restrictions")} data-testid="input-diet" /></div>
              <div className="form-group"><label>Mental health considerations (optional but encouraged)</label><textarea onChange={set("mental_health_notes")} data-testid="input-mental" /></div>

              <H n="D" t="Emergency Contact" />
              <div className="form-group"><label>Emergency Contact Name</label><input type="text" onChange={set("emergency_name")} data-testid="input-emg-name" /></div>
              <div className="form-group"><label>Relationship</label><input type="text" onChange={set("emergency_relation")} data-testid="input-emg-rel" /></div>
              <div className="form-group"><label>Emergency Contact Phone</label><input type="tel" onChange={set("emergency_phone")} data-testid="input-emg-phone" /></div>

              <H n="E" t="Required Acknowledgments" />
              <p style={{ color: "rgba(250,246,238,.5)", fontSize: ".84rem", marginBottom: 20 }}>
                The Reset is a wellness and lifestyle program. It does not diagnose, treat, cure, or prevent disease and does not replace professional medical advice. Individual results vary.
              </p>
              {REQUIRED_ACKS.map(([k, label]) => (
                <label key={k} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 14, cursor: "pointer" }} data-testid={`ack-${k}`}>
                  <input type="checkbox" onChange={set(k)} style={{ marginTop: 4, accentColor: "var(--gold)" }} />
                  <span style={{ color: "rgba(250,246,238,.75)", fontSize: ".88rem", lineHeight: 1.6 }}><AckLabel k={k} label={label} /></span>
                </label>
              ))}
              <label style={{ display: "flex", gap: 12, alignItems: "flex-start", marginTop: 20, cursor: "pointer" }} data-testid="ack-media">
                <input type="checkbox" onChange={set("ack_media_release")} style={{ marginTop: 4, accentColor: "var(--gold)" }} />
                <span style={{ color: "rgba(250,246,238,.55)", fontSize: ".84rem", lineHeight: 1.6, fontStyle: "italic" }}>
                  (Optional) I consent to being photographed/filmed during designated moments for Saviar's use.
                </span>
              </label>

              {error && <p style={{ color: "#e08b8b", fontSize: ".85rem", margin: "20px 0" }} data-testid="apply-error">{error}</p>}
              <button type="submit" disabled={busy} className="btn btn-primary" style={{ width: "100%", marginTop: 32 }} data-testid="apply-submit">
                {busy ? "Submitting…" : "Submit Application"}
              </button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
